// @ts-nocheck
// RLevents migration: notification preference columns no longer exist on profiles.
// We keep the hook surface stable but use client-side defaults only.
// This file also avoids throwing if AuthContext is missing (e.g. when rendered
// from PublicHeader which sits outside AuthProvider).
import { useContext, useState } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

export interface NotificationPreferences {
  email_notifications_enabled: boolean;
  sms_notifications_enabled: boolean;
  follow_notifications_enabled: boolean;
  profile_view_notifications_enabled: boolean;
}

const DEFAULTS: NotificationPreferences = {
  email_notifications_enabled: true,
  sms_notifications_enabled: true,
  follow_notifications_enabled: true,
  profile_view_notifications_enabled: true,
};

export const useNotificationPreferences = () => {
  // Use raw context — returns undefined outside AuthProvider instead of throwing.
  const auth = useContext(AuthContext) as any;
  const profile = auth?.profile ?? null;

  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULTS);
  const [isLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Update a specific preference (client-side only — no DB column to persist to)
  const updatePreference = async (key: keyof NotificationPreferences, value: boolean) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
    toast.success('Notification preference updated');
  };

  // Send test notification (still uses Communication Hub edge function)
  const sendTestNotification = async (type: 'email' | 'sms') => {
    if (!profile?.id) return;

    if (type === 'email' && !profile.email) {
      toast.error('No email address found for this profile');
      return;
    }
    if (type === 'sms' && !profile.phone) {
      toast.error('No phone number found for this profile');
      return;
    }

    setIsSaving(true);
    try {
      if (type === 'email') {
        const { error } = await invokeEdgeFunction('queue-communication', {
          body: {
            type: 'email',
            recipient_email: profile.email,
            subject: 'Test Notification from RecruitLook',
            content: 'This is a test notification to verify your email preferences are working correctly.',
            metadata: { event_type: 'test_notification', test: true, trigger_source: 'preferences_test' },
          },
        });
        if (error) {
          toast.error('Failed to queue test email');
          return;
        }
        toast.success('Test email queued!');
      } else {
        const { error } = await invokeEdgeFunction('queue-communication', {
          body: {
            type: 'sms',
            recipient_phone: profile.phone,
            content: 'This is a test notification to verify your SMS preferences are working correctly.',
            metadata: { event_type: 'test_notification', test: true, trigger_source: 'preferences_test' },
          },
        });
        if (error) {
          toast.error('Failed to queue test SMS');
          return;
        }
        toast.success('Test SMS queued!');
      }
    } catch (error) {
      toast.error(`Failed to queue test ${type}`);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    preferences,
    isLoading,
    isSaving,
    updatePreference,
    sendTestNotification,
  };
};
